import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { timetableApi, assignmentsApi } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Clock, CheckCircle2, AlertCircle, Info, Linkedin, Code, Sparkles, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const Dashboard = () => {
  const { user } = useAuth();
  const [timetable, setTimetable] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showCreatorInfo, setShowCreatorInfo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [newEntry, setNewEntry] = useState({
    day: 'Monday',
    start_time: '',
    end_time: '',
    subject: '',
    room: '',
    color: '#4F46E5'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [timetableRes, assignmentsRes] = await Promise.all([
        timetableApi.getAll(),
        assignmentsApi.getAll()
      ]);
      setTimetable(timetableRes.data);
      setAssignments(assignmentsRes.data.filter(a => !a.completed).slice(0, 5));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      await timetableApi.create(newEntry);
      toast.success('Class added to timetable');
      setShowAddEntry(false);
      setNewEntry({
        day: 'Monday',
        start_time: '',
        end_time: '',
        subject: '',
        room: '',
        color: '#4F46E5'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add class');
    }
  };

  const handleEditEntry = async (e) => {
    e.preventDefault();
    try {
      await timetableApi.update(editingEntry.id, editingEntry);
      toast.success('Class updated successfully');
      setShowEditEntry(false);
      setEditingEntry(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update class');
    }
  };

  const [deletingEntryId, setDeletingEntryId] = useState(null);

  const openDeleteConfirm = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteConfirm(true);
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    
    setDeletingEntryId(entryToDelete.id);
    setShowDeleteConfirm(false);
    
    try {
      await timetableApi.delete(entryToDelete.id);
      // Immediately update local state
      setTimetable(prevTimetable => prevTimetable.filter(entry => entry.id !== entryToDelete.id));
      toast.success('Class deleted successfully');
      // Also refresh from server to ensure sync
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to delete class');
    } finally {
      setDeletingEntryId(null);
      setEntryToDelete(null);
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry({ ...entry });
    setShowEditEntry(true);
  };

  const getTimetableForDay = (day) => {
    return timetable
      .filter(entry => entry.day === day)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getUpcomingDeadlines = () => {
    return assignments
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .slice(0, 3);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6 md:p-12 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">
              Welcome back, {user?.name}
            </h1>
            <p className="text-lg text-muted-foreground">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          <Dialog open={showCreatorInfo} onOpenChange={setShowCreatorInfo}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="rounded-full gap-2 border-primary/20 hover:border-primary hover:bg-primary/5"
                data-testid="creator-info-button"
              >
                <Info className="w-4 h-4" />
                About Creator
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="creator-info-dialog">
              <DialogHeader className="text-center pb-4">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mb-4">
                  <Code className="w-10 h-10 text-white" />
                </div>
                <DialogTitle className="text-2xl">Amar Saidik</DialogTitle>
                <p className="text-muted-foreground">Developer & Creator</p>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-secondary/30 rounded-xl p-4 text-center">
                  <Sparkles className="w-6 h-6 mx-auto text-primary mb-2" />
                  <p className="text-sm text-foreground leading-relaxed">
                    This student planner was created to help students organize their academic life efficiently. 
                    Connect with me to learn more about my work and projects!
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/amar-saidik"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-[#0A66C2]/5 hover:bg-[#0A66C2]/10 rounded-xl transition-all border border-[#0A66C2]/20 hover:border-[#0A66C2]/40 group"
                  data-testid="creator-linkedin-link"
                >
                  <div className="w-12 h-12 bg-[#0A66C2] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Linkedin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#0A66C2]">Connect on LinkedIn</p>
                    <p className="text-xs text-muted-foreground">View professional profile</p>
                  </div>
                  <svg className="w-4 h-4 text-[#0A66C2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-sm border-border/50" data-testid="timetable-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Clock className="w-6 h-6 text-primary" />
                  Weekly Timetable
                </CardTitle>
                <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full" size="sm" data-testid="add-timetable-button">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Class
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-timetable-dialog">
                    <DialogHeader>
                      <DialogTitle>Add Class to Timetable</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddEntry} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Day</Label>
                        <Select value={newEntry.day} onValueChange={(value) => setNewEntry({ ...newEntry, day: value })}>
                          <SelectTrigger data-testid="timetable-day-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS.map(day => (
                              <SelectItem key={day} value={day}>{day}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Start Time</Label>
                          <Input
                            type="time"
                            value={newEntry.start_time}
                            onChange={(e) => setNewEntry({ ...newEntry, start_time: e.target.value })}
                            required
                            data-testid="timetable-start-time-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Time</Label>
                          <Input
                            type="time"
                            value={newEntry.end_time}
                            onChange={(e) => setNewEntry({ ...newEntry, end_time: e.target.value })}
                            required
                            data-testid="timetable-end-time-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Subject</Label>
                        <Input
                          value={newEntry.subject}
                          onChange={(e) => setNewEntry({ ...newEntry, subject: e.target.value })}
                          placeholder="e.g. Mathematics"
                          required
                          data-testid="timetable-subject-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Room (optional)</Label>
                        <Input
                          value={newEntry.room}
                          onChange={(e) => setNewEntry({ ...newEntry, room: e.target.value })}
                          placeholder="e.g. Room 101"
                          data-testid="timetable-room-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex gap-2">
                          {COLORS.map(color => (
                            <button
                              key={color}
                              type="button"
                              className={`w-8 h-8 rounded-full border-2 ${newEntry.color === color ? 'border-foreground' : 'border-transparent'}`}
                              style={{ backgroundColor: color }}
                              onClick={() => setNewEntry({ ...newEntry, color })}
                              data-testid={`color-${color}`}
                            />
                          ))}
                        </div>
                      </div>
                      <Button type="submit" className="w-full rounded-full" data-testid="submit-timetable-entry">
                        Add to Timetable
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {DAYS.map(day => {
                    const dayEntries = getTimetableForDay(day);
                    return (
                      <div key={day} className="space-y-3">
                        <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
                          {day}
                        </h3>
                        {dayEntries.length > 0 ? (
                          <div className="space-y-4">
                            {dayEntries.map(entry => (
                              <div
                                key={entry.id}
                                className="flex items-center gap-4 p-4 rounded-xl bg-secondary/30 border border-border/50 hover:shadow-sm transition-shadow"
                                data-testid={`timetable-entry-${entry.id}`}
                              >
                                <div
                                  className="w-1 h-12 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <div className="flex-1">
                                  <p className="font-semibold text-base">{entry.subject}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {entry.start_time} - {entry.end_time}
                                    {entry.room && ` • ${entry.room}`}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditDialog(entry)}
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary opacity-70 hover:opacity-100 transition-opacity"
                                    data-testid={`edit-entry-${entry.id}`}
                                    disabled={deletingEntryId === entry.id}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDeleteConfirm(entry)}
                                    className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive opacity-70 hover:opacity-100 transition-opacity"
                                    data-testid={`delete-entry-${entry.id}`}
                                    disabled={deletingEntryId === entry.id}
                                  >
                                    {deletingEntryId === entry.id ? (
                                      <div className="w-4 h-4 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No classes scheduled</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Edit Entry Dialog */}
            <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
              <DialogContent data-testid="edit-timetable-dialog">
                <DialogHeader>
                  <DialogTitle>Edit Class</DialogTitle>
                </DialogHeader>
                {editingEntry && (
                  <form onSubmit={handleEditEntry} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Day</Label>
                      <Select 
                        value={editingEntry.day} 
                        onValueChange={(value) => setEditingEntry({ ...editingEntry, day: value })}
                      >
                        <SelectTrigger data-testid="edit-timetable-day-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS.map(day => (
                            <SelectItem key={day} value={day}>{day}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time</Label>
                        <Input
                          type="time"
                          value={editingEntry.start_time}
                          onChange={(e) => setEditingEntry({ ...editingEntry, start_time: e.target.value })}
                          required
                          data-testid="edit-timetable-start-time-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={editingEntry.end_time}
                          onChange={(e) => setEditingEntry({ ...editingEntry, end_time: e.target.value })}
                          required
                          data-testid="edit-timetable-end-time-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Subject</Label>
                      <Input
                        value={editingEntry.subject}
                        onChange={(e) => setEditingEntry({ ...editingEntry, subject: e.target.value })}
                        placeholder="e.g. Mathematics"
                        required
                        data-testid="edit-timetable-subject-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Room (optional)</Label>
                      <Input
                        value={editingEntry.room || ''}
                        onChange={(e) => setEditingEntry({ ...editingEntry, room: e.target.value })}
                        placeholder="e.g. Room 101"
                        data-testid="edit-timetable-room-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        {COLORS.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-8 h-8 rounded-full border-2 ${editingEntry.color === color ? 'border-foreground' : 'border-transparent'}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditingEntry({ ...editingEntry, color })}
                            data-testid={`edit-color-${color}`}
                          />
                        ))}
                      </div>
                    </div>
                    <Button type="submit" className="w-full rounded-full" data-testid="submit-edit-timetable-entry">
                      Save Changes
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <DialogContent data-testid="delete-confirm-dialog">
              <DialogHeader>
                <DialogTitle>Delete Class</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Are you sure you want to delete this class?
                </p>
                {entryToDelete && (
                  <div className="p-3 bg-secondary/30 rounded-lg border border-border">
                    <p className="font-semibold text-base mb-1">{entryToDelete.subject}</p>
                    <p className="text-sm text-muted-foreground">
                      {entryToDelete.day} • {entryToDelete.start_time} - {entryToDelete.end_time}
                      {entryToDelete.room && ` • ${entryToDelete.room}`}
                    </p>
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setEntryToDelete(null);
                    }}
                    data-testid="cancel-delete-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteEntry}
                    data-testid="confirm-delete-button"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="space-y-6">
            <Card className="shadow-sm border-border/50" data-testid="upcoming-assignments-card">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-warning" />
                  Due Soon
                </CardTitle>
              </CardHeader>
              <CardContent>
                {getUpcomingDeadlines().length > 0 ? (
                  <div className="space-y-3">
                    {getUpcomingDeadlines().map(assignment => (
                      <div
                        key={assignment.id}
                        className="p-4 rounded-xl bg-accent/50 border border-border/50"
                        data-testid={`upcoming-assignment-${assignment.id}`}
                      >
                        <p className="font-semibold">{assignment.title}</p>
                        <p className="text-sm text-muted-foreground">{assignment.subject}</p>
                        <p className="text-xs text-warning font-medium mt-2">
                          Due: {format(new Date(assignment.due_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-success mb-2" />
                    <p className="text-sm text-muted-foreground">All caught up!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
